// src/components/StatusIcon.jsx
// 3.68.9 (owner, Oct 28): the system status icon on the sign-in page, the
// Overview and Health, one look everywhere. The more serious, the more it
// moves: green = the shield with a tick and a calm ripple (never blinks),
// amber = the warning triangle with a small wobble, red = the block shield
// shaking. A change of level pops the icon once. The box around it can take
// statusGlow(level) for the amber/red glow. Nothing moves for people who ask
// their computer for less motion.
//   <StatusIcon level="green" size={18} />
import { useEffect, useRef, useState } from 'react';
import { FLUX_SVG } from './fluxIcons';

const ICON = { green: 'shield-check', amber: 'warning', red: 'shield-block' };   // owner: the block sign for red

const CSS = `
.st-ico { display: inline-block; position: relative; flex-shrink: 0; line-height: 0; }
.st-ico svg { display: block; }
.st-green::after { content: ""; position: absolute; inset: -2px; border-radius: 50%; border: 2px solid #43a047; opacity: 0; pointer-events: none; animation: stPing 3.2s ease-out infinite; }
@keyframes stPing { 0% { transform: scale(.6); opacity: .7; } 70%, 100% { transform: scale(1.9); opacity: 0; } }
.st-green .k1 { animation: stTick 6.4s ease-in-out infinite; }
@keyframes stTick { 0%, 8% { stroke-dashoffset: 22; } 22%, 100% { stroke-dashoffset: 0; } }
.st-amber { animation: stWobble 2.4s ease-in-out infinite; }
@keyframes stWobble { 0%, 70%, 100% { transform: none; } 78% { transform: rotate(-9deg); } 86% { transform: rotate(7deg); } 93% { transform: rotate(-3deg); } }
.st-red { animation: stShake 1.2s ease-in-out infinite; }
@keyframes stShake { 0%, 40%, 100% { transform: none; } 6% { transform: rotate(-12deg); } 12% { transform: rotate(12deg); } 18% { transform: rotate(-9deg); } 24% { transform: rotate(7deg); } 30% { transform: rotate(-3deg); } }
.st-ico.st-pop { animation: stPop .5s ease-out; }
@keyframes stPop { 0% { transform: scale(.7); } 60% { transform: scale(1.18); } 100% { transform: none; } }
.st-glow-amber { animation: stGlowAmber 2.4s ease-in-out infinite; }
@keyframes stGlowAmber { 0%, 100% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0); } 50% { box-shadow: 0 0 0 5px rgba(255, 193, 7, .28); } }
.st-glow-red { animation: stGlowRed 1.2s ease-in-out infinite; }
@keyframes stGlowRed { 0%, 100% { box-shadow: 0 0 0 0 rgba(229, 57, 53, 0); } 50% { box-shadow: 0 0 0 6px rgba(229, 57, 53, .3); } }
@media (prefers-reduced-motion: reduce) { .st-ico, .st-ico *, .st-ico::after, .st-glow-amber, .st-glow-red { animation: none !important; } }
`;
let cssAdded = false;
function addCss() {
  if (cssAdded || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.id = 'status-icon';
  el.textContent = CSS;
  document.head.appendChild(el);
  cssAdded = true;
}

// The class for the box around the icon (pill, banner): amber/red glow
export const statusGlow = level => (level === 'amber' || level === 'red' ? `st-glow-${level}` : '');

export default function StatusIcon({ level = 'green', size = 18 }) {
  addCss();
  const lv = ICON[level] ? level : 'green';
  const [pop, setPop] = useState(false);
  const prev = useRef(lv);
  useEffect(() => {
    if (prev.current === lv) return undefined;
    prev.current = lv;
    setPop(true);
    const t = setTimeout(() => setPop(false), 600);
    return () => clearTimeout(t);
  }, [lv]);
  // our own drawings from our own bundle (no outside content), so inlining them is safe
  return <span className={`st-ico st-${lv}${pop ? ' st-pop' : ''}`} style={{ width: size, height: size }} aria-hidden="true"
    dangerouslySetInnerHTML={{ __html: FLUX_SVG[ICON[lv]] }} />;
}
